import { Button } from '@dev-spendesk/grapes';
import { useState } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { type MoneyAmount, type Room } from '../../types';

type Props = {
  rooms: Room[];
  selectedRoomIndex: number;
  onSelectRoom: (index: number) => void;
};

const formatPrice = (amount: MoneyAmount): string => {
  const value = amount.amount / 10 ** amount.precision;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: amount.currency,
    minimumFractionDigits: 2,
  }).format(value);
};

const MAX_ROOMS_TO_DISPLAY = 4;

export const RoomSelection = ({
  rooms,
  selectedRoomIndex,
  onSelectRoom,
}: Props) => {
  const { t } = useTranslation('global');
  const hasCollapseAction = rooms.length > MAX_ROOMS_TO_DISPLAY;
  const [isListCollapsed, setIsListCollapsed] = useState(hasCollapseAction);

  if (rooms.length === 0) {
    return null;
  }

  const visibleRooms = isListCollapsed
    ? rooms.slice(0, MAX_ROOMS_TO_DISPLAY)
    : rooms;

  return (
    <div className="flex flex-col gap-8">
      <h3 className="title-l">
        {t('travel.propertyDetails.roomSelection.chooseRoom')}
      </h3>
      <div className="relative flex flex-col gap-8 overflow-hidden">
        {hasCollapseAction && isListCollapsed && (
          <div
            className="pointer-events-none absolute inset-0 z-1"
            style={{
              background:
                'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 25%, rgb(255, 255, 255) 120%)',
            }}
          />
        )}
        {visibleRooms.map((room, index) => (
          <RoomOption
            key={`${room.name}-${index + 1}`}
            room={room}
            isSelected={selectedRoomIndex === index}
            onSelect={() => onSelectRoom(index)}
          />
        ))}
      </div>
      {hasCollapseAction && (
        <Button
          className="mt-8 w-fit"
          fit="content"
          text={isListCollapsed ? t('misc.seeMore') : t('misc.seeLess')}
          onClick={() => setIsListCollapsed(!isListCollapsed)}
          iconName={isListCollapsed ? 'chevron-down' : 'chevron-up'}
          iconPosition="right"
          variant="tertiaryNeutral"
          hasNoHorizontalPadding
        />
      )}
    </div>
  );
};

const RoomOption = ({
  room,
  isSelected,
  onSelect,
}: {
  room: Room;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const { t } = useTranslation('global');
  const nightlyPrice = formatPrice(room.nightlyBaseFrom);
  const lowestTotal = room.rateOptions.reduce(
    (min, rate) =>
      rate.totalPrice.amount < min.amount ? rate.totalPrice : min,
    room.rateOptions[0]?.totalPrice || room.nightlyBaseFrom,
  );

  return (
    <label
      className={`rounded-12 flex cursor-pointer items-center gap-16 border p-16 ${
        isSelected
          ? 'border-border-selected bg-background-primary-selected'
          : 'border-border-default bg-background-primary-default'
      }`}
    >
      <input
        type="radio"
        name="room"
        checked={isSelected}
        onChange={onSelect}
        className={`h-16 w-16 shrink-0 rounded-full ${
          isSelected
            ? 'border-border-selected border-5'
            : 'border-border-default border'
        }`}
      />

      <div className="flex flex-1 flex-col gap-4">
        <p className="title-m">{room.name}</p>
      </div>

      <div className="flex flex-col items-end gap-4">
        <p className="title-l">
          {t('travel.propertyDetails.roomSelection.fromPrice', {
            price: formatPrice(lowestTotal),
          })}
        </p>
        <p className="body-m text-content-secondary-bg-primary">
          {t('travel.hotel.searchResult.pricePerNight', {
            price: nightlyPrice,
          })}
        </p>
      </div>
    </label>
  );
};
